import { Button, Modal } from 'antd';
import { useState, type FC, useEffect, useMemo } from 'react';
import { Check, Input, InputFile, Select } from '../../../../components';
import { parseSelect } from '../../../../utils/parseSelect';
import { type ITopic } from '../../../../types/topic';
import { type ISubject } from '../../../../types/subject';
import { type IQuestion } from '../../../../types/question';
import { useCreateQuestion, useUpdateQuestion } from '../../../../services/question';
import clsx from 'clsx';
import { useTheme } from '../../../../contexts/ThemeContext';
import { IconHelp } from '@tabler/icons-react';
import './QuestionsModal.scss';

interface Props {
  callback?: () => Promise<void>
  onClose: () => void
  open: boolean
  selectedQuestion?: IQuestion
  subjects: ISubject[]
  topics: ITopic[]
  title: string
}

const QuestionsModal: FC<Props> = ({
  callback,
  open,
  onClose,
  selectedQuestion,
  subjects,
  title,
  topics
}) => {
  const { theme } = useTheme();
  const { createQuestion, loading: createLoading } = useCreateQuestion();
  const { updateQuestion, loading: updateLoading } = useUpdateQuestion();

  const [editedTopic, setEditedTopic] = useState<string>('');
  const [editedSubject, setEditedSubject] = useState<string>('');
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [editedImage, setEditedImage] = useState<string>('');
  const [editedAnswers, setEditedAnswers] = useState<string[]>([]);
  const [editedCorrect, setEditedCorrect] = useState<string>('');
  const [editedInfo, setEditedInfo] = useState<string | undefined>('');
  const [editedPastExam, setPastExam] = useState<boolean>(false);
  const [editedRating, setEditedRating] = useState('');

  useEffect(() => {
    if (selectedQuestion?.branch) {
      setEditedSubject((selectedQuestion.subject as ISubject)._id!);
      setEditedTopic((selectedQuestion.topic as ITopic)._id!);
      setEditedTitle(selectedQuestion?.title);
      setEditedImage(selectedQuestion?.image);
      setEditedAnswers(selectedQuestion.answers);
      setEditedCorrect(selectedQuestion.correctAnswer);
      setEditedInfo(selectedQuestion?.info);
      setPastExam(selectedQuestion?.pastexam);
      setEditedRating(selectedQuestion?.rating?.toString() || '');
    }
  }, [selectedQuestion]);

  const handleClose = () => {
    setEditedTitle('');
    setEditedTopic('');
    setEditedSubject('');
    setEditedImage('');
    setEditedInfo('');
    setPastExam(false);
    onClose();
  };

  const handleSave = async () => {
    if (selectedQuestion?._id) {
      await updateQuestion({
        _id: selectedQuestion._id,
        topic: editedTopic,
        subject: editedSubject,
        title: editedTitle,
        image: editedImage,
        answers: editedAnswers,
        correctAnswer: editedCorrect,
        info: editedInfo,
        pastexam: editedPastExam,
        rating: Number(editedRating)
      });
    } else {
      await createQuestion({
        topic: editedTopic,
        subject: editedSubject,
        title: editedTitle,
        image: editedImage,
        answers: editedAnswers,
        correctAnswer: editedCorrect,
        info: editedInfo,
        pastexam: editedPastExam,
        rating: Number(editedRating)
      });
    }
    if (callback) {
      await callback();
    }
    handleClose();
  };

  const parsedTopics = useMemo(() => parseSelect({
    array: topics,
    labelKey: 'name',
    valueKey: '_id'
  }), [topics]);

  const parsedSubjects = useMemo(() => parseSelect({
    array: subjects,
    labelKey: 'name',
    valueKey: '_id'
  }), [subjects]);

  const parsedAnswers = useMemo(() => editedAnswers.map((ans) => ({
    label: ans,
    value: ans
  })), [editedAnswers]);

  const onChangeAnswer = (val: string, index: number) => {
    setEditedAnswers((prev) => prev.map((a, idx) => {
      if (index === idx) {
        return val;
      }
      return a;
    }));
  };

  const addAnswer = () => {
    const newAnswers = [...editedAnswers, ''];

    setEditedAnswers(newAnswers);
  };

  return (
    <Modal
      title={
        <div className={clsx(
          'flex items-center gap-3',
          'text-gray-900 dark:text-white'
        )}>
          <div className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            'bg-gradient-to-br from-blue-500 to-purple-500',
            'dark:from-blue-600 dark:to-purple-600'
          )}>
            <IconHelp size={20} className="text-white" />
          </div>
          <span className="text-xl font-bold">{title}</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      onOk={handleSave}
      okButtonProps={{
        disabled: !editedTitle || !editedTopic,
        loading: createLoading || updateLoading,
        className: clsx(
          'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
          'border-none shadow-md hover:shadow-lg',
          'transition-all duration-300'
        )
      }}
      cancelButtonProps={{
        className: clsx(
          'border-gray-300 dark:border-gray-600',
          'text-gray-700 dark:text-gray-300',
          'hover:border-gray-400 dark:hover:border-gray-500',
          'transition-all duration-300'
        )
      }}
      okText="Guardar"
      width={700}
      className={clsx(
        'questions-modal',
        theme === 'dark' ? 'dark-modal' : 'light-modal'
      )}
      style={{
        borderRadius: '16px',
        overflow: 'hidden'
      }}
      maskStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      wrapClassName={clsx(
        'questions-modal-wrapper',
        theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
        'bottom-sheet-mobile'
      )}
      centered={false}
    >
      <div className={clsx(
        'flex flex-col space-y-4',
        'w-full'
      )}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2 w-full">
            <Select
              placeholder="Bloque"
              onChange={setEditedSubject}
              options={parsedSubjects}
              value={editedSubject}
            />
          </div>
          <div className="flex flex-col space-y-2 w-full">
            <Select
              placeholder="Tema"
              onChange={setEditedTopic}
              options={parsedTopics}
              value={editedTopic}
            />
          </div>
        </div>

        <div className="flex flex-col space-y-2 w-full">
          <Input
            value={editedTitle}
            onChange={(e) => { setEditedTitle(e.target.value); }}
            placeholder='Título'
          />
        </div>

        <div className="flex flex-col space-y-2 w-full">
          <InputFile
            type='image'
            name='Imagen'
            fallbackUrl={(url, type) => setEditedImage(url)}
            value={editedImage}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col space-y-2 w-full">
            <Input
              type='number'
              value={editedRating}
              onChange={(e) => { setEditedRating(e.target.value); }}
              placeholder='Dificultad'
            />
          </div>
          <div className="flex flex-col space-y-2 w-full">
            <Check
              checked={editedPastExam}
              onChange={(e) => { setPastExam(e.target.checked); }}
              name='Oposición Pasada'
            />
          </div>
        </div>

        <div className="flex flex-col space-y-2 w-full">
          <Input
            value={editedInfo}
            onChange={(e) => { setEditedInfo(e.target.value); }}
            placeholder='Explicación'
          />
        </div>

        <div className="flex flex-col space-y-2 w-full">
          <div className={clsx(
            'p-4 rounded-lg border-2',
            'bg-gray-50 border-gray-200',
            'dark:bg-surface-medium dark:border-borders'
          )}>
            <h4 className={clsx(
              'text-sm font-semibold mb-3',
              'text-gray-900 dark:text-white'
            )}>
              Respuestas
            </h4>
            <div className="space-y-2">
              {editedAnswers.map((answer, index) => (
                <Input
                  key={index}
                  value={editedAnswers[index]}
                  onChange={(e) => { onChangeAnswer(e.target.value, index); }}
                  placeholder={`Respuesta ${index + 1}`}
                />
              ))}
            </div>
            <Button
              onClick={addAnswer}
              className={clsx(
                'mt-3 w-full',
                'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600',
                'text-white border-none',
                'shadow-sm hover:shadow-md',
                'transition-all duration-300'
              )}
            >
              Añadir opción
            </Button>
          </div>
        </div>

        <div className="flex flex-col space-y-2 w-full">
          <Select
            placeholder="Respuesta correcta"
            onChange={setEditedCorrect}
            options={parsedAnswers}
            value={editedCorrect}
          />
        </div>
      </div>
    </Modal>
  );
};

export { QuestionsModal };
